import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { apiFetch } from "@/lib/api";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";


type PermissionModule = {
  module: string;
  items: { key: string; label: string }[];
};

type PermissionsPayload = {
  role: { id: number; name: string };
  modules: PermissionModule[];
  selected: string[]; // permission keys
};


export function PermissionsDialog({
  open,
  onOpenChange,
  roleId,
  roleName,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  roleId: number | null;
  roleName: string;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const permsQ = useInfiniteQuery({
    enabled: open && !!roleId,
    queryKey: ["role_permissions", roleId],
    queryFn: async () => {
      const res = await apiFetch<PermissionsPayload>(`/users/roles/${roleId}/permissions`);
      return res;
    },
    initialPageParam: null,
    getNextPageParam: () => null,
  });

  const payload = permsQ.data?.pages?.[0] as PermissionsPayload | undefined;

  useEffect(() => {
    if (!open) return;
    if (payload?.selected) {
      setSelected(payload.selected);
      setSelectAll(false);
    }
  }, [open, payload?.selected]);

const saveMut = useMutation({
  mutationFn: async () => {
    if (!roleId) throw new Error("Missing roleId");

    return apiFetch(`/users/roles/${roleId}/permissions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        permissions: selected,
      }),
    });
  },

  onSuccess: async () => {
    toast.success("Permissions updated");
    await qc.invalidateQueries({
      queryKey: ["role_permissions", roleId],
    });
    onOpenChange(false);
  },

  onError: (e: any) =>
    toast.error(e?.message || "Failed to update permissions"),
});

  const allKeys = useMemo(() => {
    const mods = payload?.modules ?? [];
    return mods.flatMap((m) => m.items.map((i) => i.key));
  }, [payload?.modules]);

  const toggleAll = (checked: boolean) => {
    setSelectAll(checked);
    setSelected(checked ? allKeys : []);
  };

  const toggleOne = (key: string) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-5xl w-[95vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Group Permission • {roleName}
          </DialogTitle>
        </DialogHeader>

        {permsQ.isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading permissions...</div>
        ) : permsQ.isError ? (
          <div className="p-6 text-sm text-rose-600">
            {(permsQ.error as any)?.message || "Failed to load permissions"}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Select All */}
            <div className="flex items-center justify-between gap-2 rounded-xl border p-3">
              <div className="text-sm font-medium">Permissions</div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.length > 0 && selected.length === allKeys.length}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
                Select All
              </label>
            </div>

            {/* Permission Table */}
            <div className="max-h-[60vh] overflow-auto rounded-xl border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background border-b">
                  <tr>
                    <th className="p-3 text-left w-[240px]">Module</th>
                    <th className="p-3 text-left">Permissions</th>
                  </tr>
                </thead>
                <tbody>
                  {(payload?.modules ?? []).map((mod) => (
                    <tr key={mod.module} className="border-b align-top">
                      <td className="p-3 font-semibold">{mod.module}</td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-3">
                          {mod.items.map((it) => (
                            <label key={it.key} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={selected.includes(it.key)}
                                onChange={() => toggleOne(it.key)}
                              />
                              <span>{it.label}</span>
                            </label>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={() => onOpenChange(false)}
            disabled={saveMut.isPending}
          >
            Cancel
          </Button>

          <Button
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? "Saving..." : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}